import { Link } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { Button } from '@rudi/ui/button';
import { Input } from '@rudi/ui/input';
import { Switch } from '@rudi/ui/switch';
import { QueryState } from '../components/page';
import {
  isMissingPracticeRoute,
  useRevokePatientModelKey,
  useSaveTenantPatientModel,
  useSetPatientModelKey,
  useTenantPatientModel,
} from '../api/hooks';
import type {
  PatientModelKey,
  PatientModelSettings,
  PracticeAgentDeviceDetail,
} from '../api/types';
import { ConfirmDialog, Fact, Facts, dateText, practiceErrorText } from './practice-agents';
import { prescreeningLoadError } from './prescreening';

// The patient model reads patient text for stage 3 criteria. By default it is
// the local model, which runs on the practice computer, so the text stays in
// the practice. Each organisation can instead switch on the cloud model, and
// each computer then needs its own key for the provider. The cloud model stays
// off until the legal gates are met.

export const PATIENT_MODEL_GATE =
  'Turn this on only after the processing agreement and the § 393 SGB V answer cover the cloud model.';

export const PATIENT_MODEL_UNAVAILABLE = 'This Rudi release has no patient model settings yet.';

function writeError(error: unknown, fallback: string): string {
  return isMissingPracticeRoute(error, 'write')
    ? PATIENT_MODEL_UNAVAILABLE
    : practiceErrorText(error, fallback);
}

/** The per-organisation switch, on the tenant's page. */
export function TenantPatientModelSection({
  tenantId,
  tenantName,
}: {
  tenantId: string;
  tenantName?: string | null;
}) {
  const settings = useTenantPatientModel(tenantId);
  return (
    <section aria-label="Patient model" className="my-6 space-y-3 rounded-3xl border p-4">
      <div>
        <h2 className="text-sm font-medium">Patient model</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          The patient model reads the record text that stage 3 criteria need. The local model runs
          on the practice computer, so the text never leaves the practice. With the cloud model on,
          the practice computers send the text to the EU cloud model instead, after replacing names,
          birth dates, addresses and insurance numbers. It goes from the practice computer to the
          provider and never through Rudi.
        </p>
      </div>
      <QueryState isLoading={settings.isLoading} error={prescreeningLoadError(settings.error)}>
        {settings.data === null ? (
          <p role="status" className="text-sm text-muted-foreground">
            {PATIENT_MODEL_UNAVAILABLE}
          </p>
        ) : settings.data ? (
          <PatientModelForm
            key={`${settings.data.enabled}-${settings.data.localEnabled ?? ''}-${settings.data.model ?? ''}-${settings.data.updatedAt ?? ''}`}
            settings={settings.data}
            tenantName={tenantName ?? 'this organisation'}
          />
        ) : null}
      </QueryState>
    </section>
  );
}

function PatientModelForm({
  settings,
  tenantName,
}: {
  settings: PatientModelSettings;
  tenantName: string;
}) {
  const save = useSaveTenantPatientModel();
  const [enabled, setEnabled] = useState(settings.enabled);
  const [localEnabled, setLocalEnabled] = useState(settings.localEnabled ?? false);
  const [model, setModel] = useState(settings.model ?? '');
  const [confirming, setConfirming] = useState(false);
  // An older Rudi has no local model; the form then leaves it alone.
  const hasLocal = settings.localEnabled !== undefined;
  const localChanged = hasLocal && localEnabled !== settings.localEnabled;
  const changed =
    enabled !== settings.enabled || localChanged || model.trim() !== (settings.model ?? '');
  const endpoint = settings.baseUrl ?? settings.defaultBaseUrl;
  const modelName = model.trim() || settings.defaultModel;

  const write = async () => {
    try {
      await save.mutateAsync({
        tenantId: settings.tenantId,
        body: { enabled, model: model.trim(), ...(localChanged ? { localEnabled } : {}) },
      });
      toast.success(
        enabled === settings.enabled
          ? 'Patient model saved'
          : enabled
            ? 'Cloud model turned on'
            : 'Cloud model turned off'
      );
      setConfirming(false);
    } catch (error) {
      toast.error(writeError(error, 'Could not save the patient model'));
    }
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!changed) return;
    if (enabled && !settings.enabled) setConfirming(true);
    else void write();
  };

  return (
    <>
      <form className="space-y-4" onSubmit={submit}>
        {hasLocal && (
          <div className="space-y-2">
            <h3 className="text-xs font-medium">Local model, on the practice computer</h3>
            <label className="flex items-center gap-3 text-sm">
              <Switch
                aria-label="Local model on"
                checked={localEnabled}
                disabled={save.isPending}
                onCheckedChange={setLocalEnabled}
              />
              {localEnabled ? 'On' : 'Off'}
            </label>
            <LocalModelFacts settings={settings} />
          </div>
        )}
        <h3 className="text-xs font-medium">Cloud model</h3>
        <p className="text-xs font-medium text-destructive">{PATIENT_MODEL_GATE}</p>
        <div className="flex flex-wrap items-end gap-6">
          <label className="flex items-center gap-3 text-sm">
            <Switch
              aria-label="Cloud model on"
              checked={enabled}
              disabled={save.isPending}
              onCheckedChange={setEnabled}
            />
            {enabled ? 'On' : 'Off'}
          </label>
          <label className="flex min-w-64 flex-1 flex-col gap-1 text-xs text-muted-foreground">
            Model, empty for the default
            <Input
              aria-label="Model"
              value={model}
              maxLength={200}
              placeholder={settings.defaultModel}
              disabled={save.isPending}
              onChange={event => setModel(event.target.value)}
            />
          </label>
          <Button type="submit" disabled={!changed || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
        <Facts label="Cloud model provider">
          <Fact label="Endpoint">
            {endpoint}
            {!settings.baseUrl && <span className="text-muted-foreground"> (default)</span>}
          </Fact>
          <Fact label="Default model">{settings.defaultModel}</Fact>
          <Fact label="Last change">{dateText(settings.updatedAt)}</Fact>
        </Facts>
      </form>
      <ConfirmDialog
        open={confirming}
        title="Turn on the cloud model"
        description={`Practice computers of ${tenantName} with a key start sending patient text to ${modelName} at ${endpoint}, instead of using the local model. ${PATIENT_MODEL_GATE}`}
        confirmLabel={save.isPending ? 'Turning on…' : 'Turn on'}
        pending={save.isPending}
        destructive
        onConfirm={() => void write()}
        onClose={() => setConfirming(false)}
      />
    </>
  );
}

function sizeText(bytes: number): string {
  return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
}

/** Which local model file the practice computers download, if Rudi has one. */
function LocalModelFacts({ settings }: { settings: PatientModelSettings }) {
  const local = settings.localModel;
  return (
    <Facts label="Local model">
      <Fact label="Model">
        {local ? `${local.name} ${local.version}` : (settings.localModelName ?? 'none')}
      </Fact>
      <Fact label="File">
        {local
          ? `${sizeText(local.size)}, published ${dateText(local.uploadedAt)}`
          : 'Not published yet: stage 3 criteria stay unknown until it is'}
      </Fact>
    </Facts>
  );
}

/** The computer's own key for the cloud model provider, on its detail page. */
export function PatientModelKeySection({ device }: { device: PracticeAgentDeviceDetail }) {
  const settings = useTenantPatientModel(device.tenantId);
  return (
    <QueryState isLoading={settings.isLoading} error={prescreeningLoadError(settings.error)}>
      {settings.data === null ? (
        <p role="status" className="text-sm text-muted-foreground">
          {PATIENT_MODEL_UNAVAILABLE}
        </p>
      ) : settings.data ? (
        <PatientModelKeyForm device={device} settings={settings.data} />
      ) : null}
    </QueryState>
  );
}

function PatientModelKeyForm({
  device,
  settings,
}: {
  device: PracticeAgentDeviceDetail;
  settings: PatientModelSettings;
}) {
  const store = useSetPatientModelKey();
  const revoke = useRevokePatientModelKey();
  const [apiKey, setApiKey] = useState('');
  const [stored, setStored] = useState<PatientModelKey | null>(null);
  const [revokedAt, setRevokedAt] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const revokedDevice = device.connection === 'revoked';
  const organisation = device.tenantName ?? 'this organisation';
  const busy = store.isPending || revoke.isPending;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const value = apiKey.trim();
    if (!value) return;
    try {
      const answer = await store.mutateAsync({ deviceId: device.id, apiKey: value });
      setStored(answer);
      setRevokedAt(null);
      toast.success(`Key version ${answer.keyVersion} stored`);
    } catch (error) {
      toast.error(writeError(error, 'Could not store the key'));
    } finally {
      // The key leaves the browser once and is never shown again.
      setApiKey('');
    }
  };

  const confirmRevoke = async () => {
    try {
      await revoke.mutateAsync(device.id);
      setStored(null);
      setRevokedAt(new Date().toISOString());
      setConfirming(false);
      toast.success('Cloud model key revoked');
    } catch (error) {
      toast.error(writeError(error, 'Could not revoke the key'));
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm">
        {settings.enabled
          ? `The cloud model is on for ${organisation}, model ${settings.model || settings.defaultModel}.`
          : `The cloud model is off for ${organisation}, so this computer does not call it, even with a key.`}{' '}
        <Link
          to="/tenants/$tenantId"
          params={{ tenantId: device.tenantId }}
          className="underline underline-offset-4"
        >
          Change it on the organisation&apos;s page
        </Link>
        .
      </p>
      {!revokedDevice && (
        <form
          className="flex flex-wrap items-end gap-3 rounded-3xl border bg-card p-4"
          onSubmit={event => void submit(event)}
        >
          <label className="flex min-w-64 flex-1 flex-col gap-1 text-xs text-muted-foreground">
            API key for the cloud model provider
            <Input
              aria-label="Cloud model API key"
              type="password"
              autoComplete="new-password"
              value={apiKey}
              maxLength={512}
              disabled={busy}
              onChange={event => setApiKey(event.target.value)}
            />
          </label>
          <Button type="submit" disabled={busy || !apiKey.trim()}>
            {store.isPending ? 'Storing…' : 'Store key'}
          </Button>
        </form>
      )}
      <p className="text-xs text-muted-foreground">
        Storing a key replaces the one before it. Rudi keeps it encrypted, hands it only to this
        computer and never shows it again. Rudi does not say whether a key is stored now; the
        self-check above shows whether the computer reaches the cloud model.
      </p>
      {stored && (
        <p role="status" className="text-sm">
          Key version {stored.keyVersion} stored {dateText(stored.createdAt)}.
        </p>
      )}
      {revokedAt && (
        <p role="status" className="text-sm">
          Key revoked {dateText(revokedAt)}.
        </p>
      )}
      <Button variant="outline" disabled={busy} onClick={() => setConfirming(true)}>
        Revoke key
      </Button>
      <ConfirmDialog
        open={confirming}
        title="Revoke the cloud model key"
        description="The computer can no longer call the cloud model. Its stage 3 criteria go to the local model, or stay unknown and wait in needs review, until you store a new key."
        confirmLabel={revoke.isPending ? 'Revoking…' : 'Revoke key'}
        pending={revoke.isPending}
        destructive
        onConfirm={() => void confirmRevoke()}
        onClose={() => setConfirming(false)}
      />
    </div>
  );
}
